"use client";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { NEAREST_CITY_BASE_URL } from "../constants/typeahead-endpoints.js";
function reducer(state, action) {
    switch(action.type){
        case 'REQUEST':
            return state.cityData ? {
                ...state,
                phase: 'resolved'
            } : {
                phase: 'pending'
            };
        case 'POSITION':
            return {
                ...state,
                phase: 'gettingCity'
            };
        case 'CITY_RESOLVED':
            return {
                phase: 'resolved',
                cityData: action.cityData
            };
        case 'FAILED':
            return {
                phase: 'rejected',
                error: action.error
            };
        default:
            return state;
    }
}
const DEFAULT_PROMPT = 'Use Current Location';
function resolveDisplayText(state) {
    if ('pending' === state.phase) return 'Locating...';
    if ('rejected' !== state.phase) return DEFAULT_PROMPT;
    const code = state.error?.code;
    if ('number' != typeof code) return 'Location Sharing Disabled';
    switch(code){
        case 1:
            return 'Location access is off. Update browser settings to try again.';
        case 2:
            return 'Location Unavailable. Please check your connection or try again.';
        case 3:
            return 'Location Request Timed Out. Try Again.';
        default:
            return 'Unexpected Location Error. Please try again.';
    }
}
async function fetchNearestCity({ latitude, longitude, baseUrl }) {
    const res = await fetch(`${baseUrl}nearest-city/${latitude}/${longitude}/5?version=1`);
    const data = await res.json();
    if (void 0 === data.exactMatch) throw new Error(`Result code: ${data.resultCode}, rguid: ${data.rguid}, message: ${data.resultMessage}`);
    const { lat, lon } = data.searchedLocation ?? {};
    const displayText = data.exactMatch?.displayText;
    if (!data.exactMatch || void 0 === lat || void 0 === lon || !displayText) throw new Error('No city was matched for this latitude/longitude.');
    return {
        itemName: displayText,
        content: displayText,
        type: 'GEO',
        lat,
        lon,
        cityID: String(data.exactMatch.matchedCity.cityID)
    };
}
function useCurrentLocation({ nearestCityBaseUrl = NEAREST_CITY_BASE_URL, onError } = {}) {
    const [state, dispatch] = useReducer(reducer, {
        phase: 'idle'
    });
    const watchIdRef = useRef(null);
    const onErrorRef = useRef(onError);
    useEffect(()=>{
        onErrorRef.current = onError;
    }, [
        onError
    ]);
    const clearWatch = useCallback(()=>{
        if ('number' != typeof watchIdRef.current) {
            watchIdRef.current = null;
            return;
        }
        navigator?.geolocation?.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
    }, []);
    useEffect(()=>clearWatch, [
        clearWatch
    ]);
    const request = useCallback(()=>{
        if (state.cityData) return void dispatch({
            type: 'REQUEST'
        });
        dispatch({
            type: 'REQUEST'
        });
        if ("u" < typeof navigator || !navigator.geolocation) return void dispatch({
            type: 'FAILED',
            error: new Error('Geolocation is not supported')
        });
        watchIdRef.current = navigator.geolocation.watchPosition((position)=>{
            clearWatch();
            dispatch({
                type: 'POSITION',
                position
            });
            fetchNearestCity({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                baseUrl: nearestCityBaseUrl
            }).then((cityData)=>{
                dispatch({
                    type: 'CITY_RESOLVED',
                    cityData
                });
            }).catch((error)=>{
                onErrorRef.current?.(error);
                dispatch({
                    type: 'FAILED',
                    error: error instanceof Error ? error : new Error(String(error))
                });
            });
        }, (error)=>{
            clearWatch();
            onErrorRef.current?.(error);
            dispatch({
                type: 'FAILED',
                error
            });
        });
    }, [
        clearWatch,
        nearestCityBaseUrl,
        state.cityData
    ]);
    return {
        status: 'gettingCity' === state.phase ? 'idle' : state.phase,
        cityData: state.cityData,
        displayText: resolveDisplayText(state),
        request
    };
}
export { useCurrentLocation };
